import { Country, isFieldErrorResponse, SourceChoiceEnum, useApiTransportPricesV1EstimateCreate } from 'api';
import { useFormContext } from 'form';

import { isCollectionFromPort } from '../../utils';

const NEAREST_CITY_MAX_RADIUS = '25';

export const useTransportEstimator = () => {
  const form = useFormContext();
  const { setValue, getValues, setError, clearErrors } = form;

  const clearTransportPriceErrors = () => {
    clearErrors('transport_price');
    clearErrors('missing_transport_price');
  };

  const { mutate: createTransportPrices } = useApiTransportPricesV1EstimateCreate({
    mutation: {
      onError(error) {
        setValue('transport_price', null);
        clearTransportPriceErrors();
        const currentTransportSource = getValues('transport_cost_source');
        if (currentTransportSource === SourceChoiceEnum.ESTIMATE) {
          setValue('shipping_cost_per_load', 0);
        }
        const errorMessages: string[] = [];
        if (error.status === 422) {
          setError('missing_transport_price', {});
        } else {
          if (isFieldErrorResponse(error)) {
            Object.keys(error.response?.data).forEach((field) => {
              errorMessages.push(error.response.data[field]);
            });
          }
          setError('transport_price', {
            message: errorMessages.join(', ') || 'An error occurred while trying to fetch the transport price',
          });
        }
      },
      async onSuccess(data) {
        setValue('transport_price', data);
        clearTransportPriceErrors();
        const currentTransportSource = getValues('transport_cost_source');
        if (currentTransportSource === SourceChoiceEnum.ESTIMATE) {
          setValue('shipping_cost_per_load', data.total_cost_in_target_currency);
          setValue('shipping_cost_per_load_currency', data.target_currency);
        }
      },
    },
  });

  const estimateTransportPrice = () => {
    const values = getValues();
    const originAddress = values.origin_address;
    const destinationAddress = !isCollectionFromPort(values.demand_incoterm) ? values.destination_address : null;
    if (
      (originAddress && destinationAddress) ||
      (values.supply_incoterm &&
        values.demand_incoterm &&
        (originAddress || values.port_of_loading) &&
        (destinationAddress || values.port_of_discharge))
    ) {
      createTransportPrices({
        data: {
          container_type: values.container_type,
          currency: values.transport_cost_currency,
          demand_incoterm: values.demand_incoterm,
          destination: destinationAddress
            ? {
                country: (destinationAddress.country_code as Country) || '',
                full_address: destinationAddress.full_address || '',
                lat: destinationAddress.latitude || 0,
                lon: destinationAddress.longitude || 0,
              }
            : undefined,
          nearest_city_max_radius: NEAREST_CITY_MAX_RADIUS,
          origin: originAddress
            ? {
                country: (originAddress.country_code as Country) || '',
                full_address: originAddress.full_address || '',
                lat: originAddress.latitude || 0,
                lon: originAddress.longitude || 0,
              }
            : undefined,
          port_of_discharge: values.port_of_discharge || undefined,
          port_of_loading: values.port_of_loading || undefined,
          supply_incoterm: values.supply_incoterm,
        },
      });
    }
  };

  return { estimateTransportPrice };
};
